// pages/Admin/CompanyAdminPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, TextField, IconButton, Tooltip, Chip, Button, Avatar,
  FormControlLabel, Typography, Divider, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Apartment, Edit, Delete, CheckCircle, Cancel, Add, Search, Refresh,
  LocationOn, Storefront, AccountTree
} from '@mui/icons-material';
import MainText from '../../components/MainText';
import CleanningButton from '../../components/CleanningButton';
import CustomModal from '../../components/CustomModal';
import CustomSwitch from '../../components/CustomSwitch';
import CustomSnackBar from '../../components/CustomSnackBar';
import API, { RBAC } from '../../services/api';
import { useAuth } from '../../auth/AuthProvider';
import { addTab } from '../../tabs/tabsSlice';
import { useDispatch } from 'react-redux';
import useFixedGridColumns from './hook';
/* ---------- helpers ---------- */
const fmtDate = (dateString) => {
  if (!dateString) return '—';
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('es-419', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit'
    });
  } catch { return '—'; }
};

const ThemeDot = ({ color }) => (
  <Box
    title={color || 'Sin tema'}
    sx={{
      width: 14, height: 14, borderRadius: '50%',
      bgcolor: color || 'grey.300',
      border: '2px solid #fff',
      boxShadow: '0 0 0 1px rgba(0,0,0,0.12)',
    }}
  />
);

const LogoCell = ({ name, logo, color, isActive }) => {
  const initials = (name || '')
    .split(' ')
    .map(s => s?.[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || 'E';

  return (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ py: 0.5 }}>
      <Box sx={{ position: 'relative' }}>
        <Avatar
          src={logo || undefined}
          imgProps={{ style: { objectFit: 'cover' } }}
          sx={{ width: 46, height: 46, bgcolor: color || 'primary.main', fontSize: 16, fontWeight: 700 }}
        >
          {!logo && initials}
        </Avatar>
        <Box sx={{
          position: 'absolute', bottom: -2, right: -2,
          display: 'flex', color: isActive ? 'success.main' : 'warning.main',
          bgcolor: 'background.paper', borderRadius: '50%', p: '1px',
        }}>
          {isActive ? <CheckCircle sx={{ fontSize: 14 }} /> : <Cancel sx={{ fontSize: 14 }} />}
        </Box>
      </Box>
      <Typography sx={{ fontWeight: 700, lineHeight: 1.25 }}>{name || '—'}</Typography>
    </Stack>
  );
};

const AddressPill = ({ address }) => (
  <Box
    title={address || '—'}
    sx={{
      display: 'inline-flex', alignItems: 'center', gap: 0.5,
      px: 1, py: 0.25, borderRadius: 999, border: '1px solid',
      borderColor: 'divider', bgcolor: 'background.default',
      maxWidth: 260, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
    }}
  >
    <LocationOn fontSize="inherit" sx={{ fontSize: 16, opacity: 0.7 }} />
    <Typography variant="body2" sx={{ fontWeight: 600 }}>{address || '—'}</Typography>
  </Box>
);
const formatYMDHMS = (v) => {
  if (v === null || v === undefined) return "Sin fecha";
  const s = String(v).trim();
  if (!s) return "Sin fecha";

  // 1) Coincidencia exacta YYYY-MM-DD HH:mm:ss (o similar)
  // Esta regex es más flexible para incluir T, Z o espacios
  const fullMatch = s.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}):(\d{2}(?:\.\d+)?)(Z)?)?/
  );

  if (fullMatch) {
    // Usaremos la Date nativa para manejar la conversión de zona horaria (como 'Z' o ISO)
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, "0");
      const mi = String(d.getMinutes()).padStart(2, "0");
      const ss = String(d.getSeconds()).padStart(2, "0");

      return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
    }
  }

  // 2) Último intento: Date nativa para cualquier otro formato válido
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");

    return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
  }

  // 3) No se pudo formatear
  return "Sin fecha";
};
/* ---------- Modal form (empresa) ---------- */
function CompanyForm({ initial, onSubmit }) {
  const [form, setForm] = useState({
    name: initial?.name || '',
    label: initial?.label || '',
    tax_id: initial?.tax_id || '',
    phone: initial?.phone || '',
    email: initial?.email || '',
    address: initial?.address || '',
    is_active: initial?.is_active ?? true,
    colorTheme: initial?.colorTheme || '#10173c',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [preview, setPreview] = useState(initial?.logo || null);

  const handleFile = (file) => {
    if (!file) { setLogoFile(null); setPreview(null); return; }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = e => setPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleSave = () => onSubmit(form, logoFile);

  return (
    <Stack spacing={2} sx={{ p: 1 }}>
      <TextField size="small" label="Nombre" value={form.name}
        onChange={e => setForm(v => ({ ...v, name: e.target.value }))} />
      <TextField size="small" label="Label" value={form.label}
        onChange={e => setForm(v => ({ ...v, label: e.target.value }))} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField size="small" label="NIT" value={form.tax_id}
          onChange={e => setForm(v => ({ ...v, tax_id: e.target.value }))} sx={{ flex: 1 }} />
        <TextField size="small" label="Teléfono" value={form.phone}
          onChange={e => setForm(v => ({ ...v, phone: e.target.value }))} sx={{ flex: 1 }} />
      </Stack>
      <TextField size="small" label="Email" value={form.email}
        onChange={e => setForm(v => ({ ...v, email: e.target.value }))} />
      <TextField size="small" label="Dirección" value={form.address}
        onChange={e => setForm(v => ({ ...v, address: e.target.value }))} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
        <FormControlLabel
          control={
            <CustomSwitch
              name="is_active"
              label={form.is_active ? 'Activa' : 'Inactiva'}
              initialValue={form.is_active}
              onChange={(_, val) => setForm(v => ({ ...v, is_active: val }))}
            />
          }
          label=""
        />
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="body2" sx={{ minWidth: 70 }}>Tema</Typography>
          <input
            type="color"
            value={form.colorTheme}
            onChange={(e) => setForm(v => ({ ...v, colorTheme: e.target.value }))}
            style={{ width: 44, height: 28, border: 'none', background: 'transparent', cursor: 'pointer' }}
          />
          <ThemeDot color={form.colorTheme} />
        </Stack>
      </Stack>
      <Divider />
      <Stack direction="row" spacing={2} alignItems="center">
        <Avatar
          src={preview || undefined}
          imgProps={{ style: { objectFit: 'cover' } }}
          sx={{ width: 68, height: 68, bgcolor: form.colorTheme, fontWeight: 700 }}
        >
          {!preview && (form.name || 'E').slice(0, 2).toUpperCase()}
        </Avatar>
        <Button variant="outlined" component="label">
          Subir logo
          <input hidden type="file" accept="image/*" onChange={e => handleFile(e.target.files?.[0])} />
        </Button>
        {preview && (
          <Button size="small" onClick={() => handleFile(null)}>Quitar</Button>
        )}
      </Stack>
      <Box sx={{ textAlign: 'right' }}>
        <Button variant="contained" onClick={handleSave}>Guardar</Button>
      </Box>
    </Stack>
  );
}

/* ---------- Modal form (bodega) ---------- */
function WarehouseForm({ company, onSubmit }) {
  const [form, setForm] = useState({ name: '', code: '', address: '', is_active: true });
  const handleSave = () => onSubmit(form);
  return (
    <Stack spacing={2} sx={{ p: 1 }}>
      <Typography variant="subtitle2">Empresa: {company?.name || '—'}</Typography>
      <TextField size="small" label="Nombre de bodega" value={form.name} onChange={e => setForm(v => ({ ...v, name: e.target.value }))} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <TextField size="small" label="Código" value={form.code} onChange={e => setForm(v => ({ ...v, code: e.target.value }))} sx={{ flex: 1 }} />
        <TextField size="small" label="Dirección" value={form.address} onChange={e => setForm(v => ({ ...v, address: e.target.value }))} sx={{ flex: 2 }} />
      </Stack>
      <FormControlLabel
        control={<CustomSwitch name="is_active" label={form.is_active ? 'Activa' : 'Inactiva'} initialValue={form.is_active} onChange={(_, val) => setForm(v => ({ ...v, is_active: val }))} />}
        label=""
      />
      <Box sx={{ textAlign: 'right' }}>
        <Button variant="contained" onClick={handleSave}>Crear bodega</Button>
      </Box>
    </Stack>
    );
  }

/* ---------- Modal manager (bodega interna) ---------- */
function LogicalWarehouseManager({ company }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ id: null, name: '', code: '', is_active: true });
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });

  const resetForm = () => setForm({ id: null, name: '', code: '', is_active: true });

  const loadData = useCallback(async () => {
    if (!company?.id) return;
    setLoading(true);
    try {
      const logicalRes = await RBAC.logicalWarehouses.list({ company: company.id });
      setRows(Array.isArray(logicalRes.data) ? logicalRes.data : []);
    } catch (err) {
      setRows([]);
      setSnack({ open: true, message: 'No se pudieron cargar las bodegas internas.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [company]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async () => {
    if (!company?.id) return;
    const name = form.name.trim();
    if (!name) {
      setSnack({ open: true, message: 'Nombre requerido.', severity: 'error' });
      return;
    }
    const payload = {
      name,
      code: form.code.trim() || null,
      is_active: !!form.is_active,
    };
    try {
      if (form.id) {
        await RBAC.logicalWarehouses.update(form.id, payload);
        setSnack({ open: true, message: 'Bodega interna actualizada.', severity: 'success' });
      } else {
        await RBAC.logicalWarehouses.create({ ...payload, company_id: company.id });
        setSnack({ open: true, message: 'Bodega interna creada.', severity: 'success' });
      }
      resetForm();
      await loadData();
    } catch (err) {
      setSnack({ open: true, message: 'No se pudo guardar la bodega interna.', severity: 'error' });
    }
  };

  const handleEdit = (row) => {
    setForm({
      id: row.id,
      name: row.name || '',
      code: row.code || '',
      is_active: row.is_active !== false,
    });
  };

  const handleToggleActive = async (row) => {
    try {
      await RBAC.logicalWarehouses.update(row.id, { is_active: !row.is_active });
      await loadData();
    } catch {
      setSnack({ open: true, message: 'No se pudo actualizar el estado.', severity: 'error' });
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm('¿Eliminar la bodega interna?')) return;
    try {
      await RBAC.logicalWarehouses.remove(row.id);
      await loadData();
      setSnack({ open: true, message: 'Bodega interna eliminada.', severity: 'success' });
    } catch {
      setSnack({ open: true, message: 'No se pudo eliminar la bodega interna.', severity: 'error' });
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
        <Button variant="outlined" startIcon={<Refresh />} onClick={loadData}>
          Recargar
        </Button>
      </Stack>

      <Divider />

      <Stack spacing={1.5}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <TextField
            size="small"
            label="Nombre"
            value={form.name}
            onChange={(e) => setForm((v) => ({ ...v, name: e.target.value }))}
            sx={{ flex: 2 }}
          />
          <TextField
            size="small"
            label="Código"
            value={form.code}
            onChange={(e) => setForm((v) => ({ ...v, code: e.target.value }))}
            sx={{ flex: 1 }}
          />
        </Stack>

        <FormControlLabel
          control={
            <CustomSwitch
              name="is_active"
              label={form.is_active ? 'Activa' : 'Inactiva'}
              initialValue={form.is_active}
              onChange={(_, val) => setForm((v) => ({ ...v, is_active: val }))}
            />
          }
          label=""
        />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          {form.id && (
            <Button variant="outlined" onClick={resetForm}>
              Cancelar
            </Button>
          )}
          <Button variant="contained" startIcon={<Add />} onClick={handleSave}>
            {form.id ? 'Guardar cambios' : 'Crear bodega interna'}
          </Button>
        </Stack>
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Código</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      Sin bodegas internas registradas.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>{row.code || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.is_active ? 'Activa' : 'Inactiva'}
                        size="small"
                        color={row.is_active ? 'success' : 'default'}
                        variant={row.is_active ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleEdit(row)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={row.is_active ? 'Desactivar' : 'Activar'}>
                        <IconButton
                          size="small"
                          color={row.is_active ? 'warning' : 'success'}
                          onClick={() => handleToggleActive(row)}
                        >
                          {row.is_active ? <Cancel fontSize="small" /> : <CheckCircle fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      <CustomSnackBar
        open={snack.open}
        setOpen={(val) => setSnack((prev) => ({ ...prev, open: val }))}
        message={snack.message}
        severity={snack.severity}
      />
    </Stack>
  );
}

/* ---------- Página ---------- */
export default function CompanyAdminPage() {
  const { isSuperuser } = useAuth();
  const dispatch = useDispatch();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState({ search: '', active: true });
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const [warehouseDialogOpen, setWarehouseDialogOpen] = useState(false);
  const [companyForWarehouse, setCompanyForWarehouse] = useState(null);
  const [logicalWarehouseDialogOpen, setLogicalWarehouseDialogOpen] = useState(false);
  const [companyForLogicalWarehouse, setCompanyForLogicalWarehouse] = useState(null);

  useEffect(() => {
    dispatch(addTab({ label: 'Empresas', path: '/admin/companies' }));
  }, [dispatch]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search || ''), 450);
    return () => clearTimeout(t);
  }, [filters.search]);

  const handleFilter = useCallback((name, value) => {
    setFilters(v => ({ ...v, [name]: value }));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let data = [];
      const activeParam = filters.active ? 'true' : 'false';

      // si eres superuser, trae todas
      const res = await RBAC.companies.list({
        search: debouncedSearch || undefined,
        active: activeParam,
      });
      data = Array.isArray(res.data) ? res.data : [];

      setRows((data || []).map(c => ({ id: c.id, ...c })));
      console.log('Companies loaded:', rows);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filters.active]);

  useEffect(() => { load(); }, [load]);

  const openCreate = useCallback(() => { setEditRow(null); setDialogOpen(true); }, []);
  const openEdit = useCallback((row) => { setEditRow(row); setDialogOpen(true); }, []);

  const handleSubmit = useCallback(async (payload, logoFile) => {
    try {
      if (logoFile) {
        const fd = new FormData();
        Object.entries(payload).forEach(([k, v]) => fd.append(k, v == null ? '' : v));
        fd.append('logo', logoFile);
        const config = { headers: { 'Content-Type': 'multipart/form-data' } };
        if (editRow) await API.patch(`company/companies/${editRow.id}/`, fd, config);
        else await API.post('company/companies/', fd, config);
      } else {
        if (editRow) await RBAC.companies.update(editRow.id, payload);
        else await RBAC.companies.create(payload);
      }
      setDialogOpen(false);
      load();
    } catch { /* snackbar si aplica */ }
  }, [editRow, load]);

  const toggleActive = useCallback(async (row) => {
    try { await RBAC.companies.update(row.id, { is_active: !row.is_active }); load(); } catch { }
  }, [load]);

  const removeRow = useCallback(async (row) => {
    if (!window.confirm('¿Eliminar empresa?')) return;
    try { await RBAC.companies.remove(row.id); load(); } catch { }
  }, [load]);

  const openCreateWarehouse = useCallback((company) => {
    setCompanyForWarehouse(company);
    setWarehouseDialogOpen(true);
  }, []);

  const openLogicalWarehouseManager = useCallback((company) => {
    setCompanyForLogicalWarehouse(company);
    setLogicalWarehouseDialogOpen(true);
  }, []);

  const handleCreateWarehouse = useCallback(async (payload) => {
    if (!companyForWarehouse) return;
    try {
      await RBAC.warehouses.create({ ...payload, company_id: companyForWarehouse.id });
      setWarehouseDialogOpen(false);
    } catch { }
  }, [companyForWarehouse]);

  /* --------- columnas (FIJAS con hook anti-bucle) --------- */
  const columns = useFixedGridColumns(() => ([
    { field: 'id', headerName: 'ID', width: 80 },
    {
      field: 'name', headerName: 'Empresa', flex: 1, minWidth: 260,
      renderCell: ({ row }) => (
        <LogoCell name={row.name} logo={row.logo} color={row.colorTheme} isActive={row.is_active} />
      )
    },
    {
      field: 'address', headerName: 'Dirección', width: 320, sortable: false,
      renderCell: ({ value }) => <AddressPill address={value} />,
    },
    {
      field: 'tax_id',
      headerName: 'NIT',
      width: 140,
      renderCell: (params) => {
        const formatNIT = (nit) => {
          if (!nit) return '';
          // Mantiene solo números y 'K', luego aplica formato con guión[citation:5]
          const cleanNit = nit.toString().toUpperCase().replace(/[^0-9K]/g, '');
          if (cleanNit.length <= 8) return cleanNit;
          // Formato: XXXXXXX-X o XXXXXXX-K
          return `${cleanNit.slice(0, -1)}-${cleanNit.slice(-1)}`;
        };
        return (
          <Typography variant="body2" sx={{ fontFamily: 'monospace', letterSpacing: '0.5px' }}>
            {formatNIT(params.value)}
          </Typography>
        );
      }
    },
    {
      field: 'phone',
      headerName: 'Teléfono',
      width: 140,
      renderCell: (params) => {
        const formatPhoneGT = (phone) => {
          if (!phone) return '';
          const digits = phone.replace(/\D/g, '');
          // Asume que un número válido tiene 8 dígitos y añade prefijo[citation:5]
          if (digits.length === 8) {
            return `+502 ${digits.substring(0, 4)} ${digits.substring(4)}`;
          }
          return `+502 ${digits}`; // Fallback para números con formato diferente
        };
        return (
          <Typography
            variant="body2"
            sx={{
              color: 'text.primary',
              '&:hover': { color: 'primary.main', textDecoration: 'underline' }
            }}
            component="a"
            href={`tel:+502${params.value?.replace(/\D/g, '')}`}
          >
            {formatPhoneGT(params.value)}
          </Typography>
        );
      }
    },
    {
      field: 'email', headerName: 'Email', width: 240,
      renderCell: (p) => (
        <Typography
          noWrap variant="body2"
          sx={{ fontWeight: 500, color: 'info.main', textDecoration: p.value ? 'underline' : 'none', cursor: p.value ? 'pointer' : 'default' }}
          onClick={() => p.value && (window.location.href = `mailto:${p.value}`)}
        >
          {p.value || 'sin email'}
        </Typography>
      )
    },
    {
      field: 'colorTheme', headerName: 'Tema', width: 70, sortable: false,
      headerAlign: 'center', align: 'center',
      renderCell: ({ row }) => <ThemeDot color={row.colorTheme} />
    },
    {
      field: "createdAt",
      headerName: "Creado",
      width: 130,
      renderCell: (params) => {
        // Utiliza un formato de fecha local para Guatemala (DD/MM/AAAA)[citation:3]
        const formatDateGT = (dateString) => {
          if (!dateString) return 'Sin fecha';
          try {
            const date = new Date(dateString);
            // Usa locale 'es-GT' para Guatemala[citation:3]
            return date.toLocaleDateString('es-GT', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            });
          } catch {
            return 'Fecha inválida';
          }
        };
        return (
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            width: '100%'
          }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontStyle: 'italic',
                fontSize: '0.75rem',
                // Estilo para diferenciar visualmente las fechas
                backgroundColor: (theme) => theme.palette.mode === 'light' ? '#f5f5f5' : '#2d2d2d',
                px: 1,
                py: 0.5,
                borderRadius: 1
              }}
            >
              {formatDateGT(params.row?.createdAt)}
            </Typography>
          </Box>
        );
      }
    },
    // *** ÚNICA columna de acciones (sin duplicados) ***
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Acciones',
      width: 160,
      getActions: (params) => ([
        <GridActionsCellItem
          key="edit"
          icon={<Edit fontSize="small" color="primary" />}
          label="Editar"
          onClick={() => openEdit(params.row)}
          showInMenu={false}
        />,
        <GridActionsCellItem
          key="toggle"
          icon={params.row.is_active
            ? <Cancel fontSize="small" color="error" />
            : <CheckCircle fontSize="small" color="success" />}
          label={params.row.is_active ? 'Desactivar' : 'Activar'}
          onClick={() => toggleActive(params.row)}
          showInMenu={false}
        />,
          <GridActionsCellItem
            key="ware"
            icon={<Storefront fontSize="small" />}
            label="Bodegas"
            onClick={() => openCreateWarehouse(params.row)}
            showInMenu={false}
          />,
          <GridActionsCellItem
            key="logical-ware"
            icon={<AccountTree fontSize="small" />}
            label="Bodegas internas"
            onClick={() => openLogicalWarehouseManager(params.row)}
            showInMenu={false}
          />,
          (isSuperuser ? (
            <GridActionsCellItem
              key="del"
            icon={<Delete fontSize="small" color="error" />}
            label="Eliminar"
            onClick={() => removeRow(params.row)}
            showInMenu={false}
          />
        ) : null)
      ].filter(Boolean)),
    },
  ]));

  /* --------- layout --------- */
  const clearAll = useCallback(() => setFilters({ search: '', active: true }), []);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Encabezado */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2, gap: 2 }}
      >
        <MainText title="Empresas" subtitle="Administración de empresas en el sistema." icon={Apartment} width="auto" />

        {/* Chips + limpiar */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {filters.search && (
              <Chip label={`Búsqueda: "${filters.search}"`} onDelete={() => handleFilter('search', '')} />
            )}
            {!filters.active && (
              <Chip label="Inactivas" color="error" onDelete={() => handleFilter('active', true)} />
            )}
            {!filters.search && filters.active && (
              <Chip label="Mostrando todas las empresas" color="info" />
            )}
          </Box>
          <CleanningButton onClick={clearAll} />
        </Stack>
      </Stack>

      {/* Barra de filtros */}
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" gap={1.5} sx={{ mb: 1 }}>
        <TextField
          size="small"
          placeholder="Buscar nombre, NIT, email..."
          value={filters.search}
          onChange={(e) => handleFilter('search', e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{ minWidth: { xs: '100%', md: 320 } }}
        />

        <CustomSwitch
          name="active"
          label={filters.active ? 'Activas' : 'Inactivas'}
          initialValue={filters.active}
          onChange={(_, val) => handleFilter('active', val)}
        />

        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Refrescar">
            <IconButton onClick={load} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button
            onClick={openCreate}
            startIcon={<Add />}
            size="small"
            variant="contained"
            sx={{
              borderRadius: 2, px: 1.5, textTransform: 'none', fontWeight: 700,
              boxShadow: 'none', '&:hover': { boxShadow: 'none', transform: 'translateY(-1px)' }
            }}
          >
            Crear
          </Button>
        </Box>
      </Stack>

      {/* Tabla */}
      <Box sx={{ height: 460, width: '100%' }}>
        <DataGrid
          rows={rows}
          getRowId={(r) => r.id}
          columns={columns}
          loading={loading}
          disableColumnFilter
          disableColumnMenu
          disableColumnReorder
          pageSizeOptions={[10, 20, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
          rowHeight={58}
          columnHeaderHeight={50}
          sx={{
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#10173c', color: '#fff' },
            '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:hover': {
              backgroundColor: '#10173c !important',
            },
            '& .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-menuIcon, & .MuiDataGrid-columnSeparator':
              { color: '#ffffff !important' },
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid #f0f0f0',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(16,23,60,.08)', cursor: 'pointer' },
          }}
        />
      </Box>

      {/* Modales */}
      <CustomModal
        open={dialogOpen}
        setOpen={setDialogOpen}
        title={editRow ? 'Editar empresa' : 'Crear empresa'}
        component={CompanyForm}
        componentProps={{ initial: editRow, onSubmit: handleSubmit }}
        width="560px"
      />

        <CustomModal
          open={warehouseDialogOpen}
          setOpen={setWarehouseDialogOpen}
          title={'Crear bodega'}
          component={WarehouseForm}
          componentProps={{ company: companyForWarehouse, onSubmit: handleCreateWarehouse }}
          width="520px"
        />

        <CustomModal
          open={logicalWarehouseDialogOpen}
          setOpen={setLogicalWarehouseDialogOpen}
          title={'Bodegas internas'}
          component={LogicalWarehouseManager}
          componentProps={{ company: companyForLogicalWarehouse }}
          width="860px"
        />
      </Box>
    );
  }
